// src/utils/schoolSettings.js
//
// District tenancy Phase 3e: per-school settings live on schools/{schoolId}.
// District-wide catalogs (serviceProviders master list, security,
// calendarSync, gmailApi) stay on app_settings/global. Theme is per-school
// on schools/{schoolId}.theme (app_settings/theme is a read-only fallback).
// Schedule import,
// Aeries mode, co-teaching rules, and proctor tabs are per-school.
//
// See Developer/DISTRICT_TENANCY_DESIGN.md §4.1.

/** Fields stored on schools/{schoolId} instead of app_settings/global. */
export const SCHOOL_SCOPED_SETTING_KEYS = [
  'grades',
  'numPeriods',
  'periodLabels',
  'hasZeroPeriod',
  'classServices',
  'sdcSubcategories',
  'customClassServices',
  'providerIds',
  'timeTables',
  'schoolCalendar',
  // Schedule import / Aeries (per-school — each site has its own source mode & mappings)
  'scheduleSource',
  'scheduleSourceOfTruth',
  'aeriesMode',
  'programFieldMapping',
  // Co-teaching / class-service rules (also loadable via schoolIntegrations.js)
  'coTeachingRules',
  // SEIS import — per-school provider column → abbreviation overrides
  'seisImport',
  // Import source-of-truth flow (which system wins per data area)
  'importFlow',
  // Last student-import timestamps (per school — each site runs its own files)
  'lastScheduleImportAt',
  'lastScheduleImportBy',
  'lastSeisImportAt',
  'lastSeisImportBy',
  'lastAeriesContactsImportAt',
  'lastAeriesContactsImportBy',
  'lastAeries504ImportAt',
  'lastAeries504ImportBy',
  'lastSeisImportResult',
  'lastScheduleImportResult',
  'lastAeriesContactsImportResult',
  'lastAeries504ImportResult',
  // School-only custom service providers (added on top of the district catalog)
  'schoolCustomProviders',
  // In-app proctor custom tabs for Testing Accommodations
  'proctorCustomTabs',
  // Teacher feedback defaults (edit-after-submit, peer comments while filling) —
  // each site runs its own IEP process, so this is not a district-wide policy
  'feedbackTeacherView'
]

/**
 * School-scoped keys that record what a school *did*, not how it is configured.
 * Seeding these from district defaults would claim every school ran an import
 * it never ran, so they are excluded from stamping.
 */
export const SCHOOL_ACTIVITY_KEYS = [
  'lastScheduleImportAt',
  'lastScheduleImportBy',
  'lastSeisImportAt',
  'lastSeisImportBy',
  'lastAeriesContactsImportAt',
  'lastAeriesContactsImportBy',
  'lastAeries504ImportAt',
  'lastAeries504ImportBy',
  'lastSeisImportResult',
  'lastScheduleImportResult',
  'lastAeriesContactsImportResult',
  'lastAeries504ImportResult'
]

/**
 * Keys a school must define itself — never inherited, never seeded.
 *
 * These point at named staff, periods, and sections at one campus. Copying them
 * to a second school does not give it a head start, it gives it rules aimed at
 * teachers who do not work there and a bell schedule it does not run.
 */
export const SCHOOL_ONLY_SETTING_KEYS = ['coTeachingRules', 'proctorCustomTabs']

/** School-scoped configuration — the keys a new school can inherit as a starting point. */
export const SCHOOL_SCOPED_CONFIG_KEYS = SCHOOL_SCOPED_SETTING_KEYS.filter(
  (key) => !SCHOOL_ACTIVITY_KEYS.includes(key) && !SCHOOL_ONLY_SETTING_KEYS.includes(key)
)

/**
 * Which school-scoped settings this school actually owns.
 *
 * Everything else is being read from the district defaults, which is invisible
 * in the UI unless something says so — and stays invisible right up until an
 * edit forks a private copy of settings the admin believed were shared.
 *
 * @param {Record<string, unknown>|null|undefined} schoolDoc
 * @returns {string[]}
 */
export function schoolOwnedSettingKeys(schoolDoc) {
  if (!schoolDoc || typeof schoolDoc !== 'object') return []
  return SCHOOL_SCOPED_SETTING_KEYS.filter((key) => schoolDoc[key] !== undefined)
}

/**
 * The settings a school is currently inheriting, ready to be written onto its
 * own document so it owns them outright.
 *
 * Idempotent: a key the school already defines is never touched, so this can
 * be re-run safely and can never overwrite a school's own configuration.
 *
 * @param {Object} args
 * @param {Record<string, unknown>} args.districtDefaults - what the school reads today
 * @param {Record<string, unknown>|null|undefined} args.schoolDoc
 * @param {string[]} [args.keys] - defaults to the config keys (never activity)
 * @returns {Record<string, unknown>} keys to write; empty when nothing is inherited
 */
export function buildSchoolSettingsStamp({
  districtDefaults = {},
  schoolDoc = {},
  keys = SCHOOL_SCOPED_CONFIG_KEYS
} = {}) {
  const stamp = {}
  for (const key of keys) {
    if (schoolDoc?.[key] !== undefined) continue
    const value = districtDefaults?.[key]
    if (value === undefined || value === null) continue
    stamp[key] = value
  }
  return stamp
}

/**
 * Extract only the school-scoped keys from a document (schools doc or save patch).
 * @param {Record<string, unknown>|null|undefined} source
 * @returns {Record<string, unknown>}
 */
export function pickSchoolSettings(source = {}) {
  if (!source || typeof source !== 'object') return {}
  const picked = {}
  for (const key of SCHOOL_SCOPED_SETTING_KEYS) {
    if (source[key] !== undefined) picked[key] = source[key]
  }
  return picked
}

/**
 * Split a save payload into school-scoped vs district-global parts.
 * @param {Record<string, unknown>} patch
 * @returns {{ school: Record<string, unknown>, global: Record<string, unknown> }}
 */
export function splitSettingsPatch(patch = {}) {
  const school = pickSchoolSettings(patch)
  const global = {}
  for (const [key, value] of Object.entries(patch)) {
    if (!SCHOOL_SCOPED_SETTING_KEYS.includes(key)) global[key] = value
  }
  return { school, global }
}

/** Roles that may write app_settings/* and emailConfig. Matches firestore.rules. */
export const DISTRICT_SETTINGS_WRITER_ROLES = ['admin', 'district_admin']

export const DISTRICT_SETTINGS_WRITE_DENIED =
  'District settings can only be changed by a district admin.'

export const NO_SCHOOL_FOR_SETTINGS =
  'Select a school before saving school settings. Grades, periods, and class services belong to a school.'

export function canWriteDistrictSettings(role) {
  return DISTRICT_SETTINGS_WRITER_ROLES.includes(String(role || ''))
}

export function assertCanWriteDistrictSettings(role) {
  if (!canWriteDistrictSettings(role)) {
    throw new Error(DISTRICT_SETTINGS_WRITE_DENIED)
  }
}

/**
 * Decide which buckets a settings save may persist.
 * School-tier writes school keys only. District-tier may also write global.
 * App Settings sends the whole form, so leftover global keys on a school-tier
 * save are dropped (not thrown) so grades/periods still save.
 *
 * @param {Record<string, unknown>} settings
 * @param {{ role?: string|null, schoolId?: string|null }} opts
 */
export function planSettingsWrite(settings, { role = null, schoolId = null } = {}) {
  const { school, global } = splitSettingsPatch(settings || {})
  const canGlobal = canWriteDistrictSettings(role)
  const hasSchool = typeof schoolId === 'string' && schoolId.trim() !== ''
  return {
    schoolPatch: school,
    globalPatch: canGlobal ? global : {},
    writeSchoolKeysToGlobalFallback: canGlobal && !hasSchool,
    droppedGlobalKeys: canGlobal ? [] : Object.keys(global)
  }
}

/**
 * Firestore `setDoc(..., { merge: true })` merges nested maps key-by-key and
 * never drops omitted keys. Stamp keys that exist in `prevMap` but not
 * `nextMap` with `deletedValue` (usually `deleteField()`) so a save can
 * actually clear them.
 *
 * @param {Record<string, unknown>|null|undefined} nextMap
 * @param {Record<string, unknown>|null|undefined} prevMap
 * @param {unknown} deletedValue
 * @returns {Record<string, unknown>}
 */
export function withRemovedMapKeys(nextMap = {}, prevMap = {}, deletedValue) {
  const next = nextMap && typeof nextMap === 'object' && !Array.isArray(nextMap) ? nextMap : {}
  const prev = prevMap && typeof prevMap === 'object' && !Array.isArray(prevMap) ? prevMap : {}
  const written = { ...next }
  for (const key of Object.keys(prev)) {
    if (!Object.prototype.hasOwnProperty.call(next, key)) {
      written[key] = deletedValue
    }
  }
  return written
}

/**
 * Apply {@link withRemovedMapKeys} to nested maps on a school settings patch
 * that the App Settings UI can shrink (SEIS provider column overrides).
 *
 * @param {Record<string, unknown>} schoolPatch
 * @param {Record<string, unknown>|null|undefined} prevSchool
 * @param {unknown} deletedValue
 * @returns {Record<string, unknown>}
 */
export function withRemovedSchoolMapKeys(schoolPatch, prevSchool, deletedValue) {
  if (!schoolPatch || typeof schoolPatch !== 'object') return schoolPatch
  const out = { ...schoolPatch }
  if (schoolPatch.seisImport && 'providerColumnMappings' in schoolPatch.seisImport) {
    out.seisImport = {
      ...schoolPatch.seisImport,
      providerColumnMappings: withRemovedMapKeys(
        schoolPatch.seisImport.providerColumnMappings,
        prevSchool?.seisImport?.providerColumnMappings,
        deletedValue
      )
    }
  }
  return out
}

/**
 * Merge district-global settings with a school's overrides.
 *
 * Provider catalog model (district tenancy):
 * - Standard catalog: globalSettings.serviceProvidersDetails (built-in list).
 * - District customs: globalSettings.customServiceProviders — managed at district
 *   level, visible to every school.
 * - School customs: schoolDoc.schoolCustomProviders — visible only to that school.
 * - Enablement: schoolDoc.providerIds (lowercase abbreviations) picks which of the
 *   combined catalog the school actually uses; unset means "everything enabled".
 *
 * Consumers read the effective `serviceProviders` (enabled standard abbreviations),
 * `customServiceProviders` (enabled customs, district + school), and
 * `serviceProvidersDetails` (label lookup) — same shape as before the split.
 *
 * @param {Record<string, unknown>} globalSettings
 * @param {Record<string, unknown>|null|undefined} schoolDoc
 * @returns {Record<string, unknown>}
 */
export function mergeEffectiveSettings(globalSettings = {}, schoolDoc = {}) {
  const merged = { ...globalSettings, ...pickSchoolSettings(schoolDoc) }
  for (const key of SCHOOL_ACTIVITY_KEYS) {
    if (schoolDoc?.[key] === undefined) delete merged[key]
  }

  // importFlow merges per key (school overrides district per field), not
  // wholesale — a school that sets one radio keeps district values for the rest.
  if (globalSettings.importFlow || schoolDoc?.importFlow) {
    merged.importFlow = {
      ...(globalSettings.importFlow || {}),
      ...(schoolDoc?.importFlow || {})
    }
  }

  // Combined custom catalog: district-wide customs first, then school-only customs.
  // Dedupe by abbreviation (district entry wins a collision).
  const districtCustoms = Array.isArray(globalSettings.customServiceProviders)
    ? globalSettings.customServiceProviders
    : []
  const schoolCustoms = Array.isArray(schoolDoc?.schoolCustomProviders)
    ? schoolDoc.schoolCustomProviders
    : []
  const seenCustom = new Set()
  const allCustoms = []
  for (const p of [...districtCustoms, ...schoolCustoms]) {
    const key = String(p?.abbreviation ?? '').toLowerCase()
    if (!key || seenCustom.has(key)) continue
    seenCustom.add(key)
    allCustoms.push(p)
  }
  merged.customServiceProviders = allCustoms

  const providerIds = schoolDoc?.providerIds
  if (Array.isArray(providerIds) && providerIds.length > 0) {
    const active = new Set(providerIds.map((id) => String(id).toLowerCase()))
    // Prefer the district catalog; if Firestore never stored details, fall back
    // to whatever abbreviations are already on the global doc so enablement
    // still produces a usable serviceProviders list for user/student dropdowns.
    let details = Array.isArray(globalSettings.serviceProvidersDetails)
      ? globalSettings.serviceProvidersDetails
      : []
    if (details.length === 0 && Array.isArray(globalSettings.serviceProviders)) {
      details = globalSettings.serviceProviders.map((abbr) =>
        typeof abbr === 'string'
          ? { abbreviation: abbr, name: abbr }
          : {
              abbreviation: abbr?.abbreviation || String(abbr || ''),
              name: abbr?.name || abbr?.abbreviation || String(abbr || '')
            }
      )
    }
    const filtered = details.filter((d) => {
      const abbr = String(d?.abbreviation ?? '').toLowerCase()
      const id = String(d?.id ?? '').toLowerCase()
      return active.has(abbr) || (id && active.has(id))
    })
    if (filtered.length > 0) {
      merged.serviceProvidersDetails = filtered
      merged.serviceProviders = filtered.map((d) => d.abbreviation).filter(Boolean)
    }
    merged.customServiceProviders = allCustoms.filter((p) =>
      active.has(String(p?.abbreviation ?? '').toLowerCase())
    )
  }

  // If nothing enabled the abbreviation list (empty legacy array, missing
  // catalog), expose details abbreviations so consumers are never blank when
  // a catalog exists.
  if (
    (!Array.isArray(merged.serviceProviders) || merged.serviceProviders.length === 0) &&
    Array.isArray(merged.serviceProvidersDetails) &&
    merged.serviceProvidersDetails.length > 0
  ) {
    merged.serviceProviders = merged.serviceProvidersDetails
      .map((d) => d?.abbreviation)
      .filter(Boolean)
  }

  return merged
}
